import React from 'react';

import Heading from '@react/react-spectrum/Heading';
import Button from '@react/react-spectrum/Button';
import TextField from '@react/react-spectrum/Textfield';
import { Grid } from '@material-ui/core';

import './ManageBannerAdminsPage.css';
import { getBannerAdmins, addBannerAdmin, removeBannerAdmin } from '../../../providers/BannerAdminAPI';
import { BannerAdminUser } from '../../../models/banner/BannerAdminUser';
import InformationDialog from '../../../utils/Dialogs/InformationDialog';
import ConfirmationDialog from '../../../utils/Dialogs/ConfirmationDialog';

const ADOBE_EMAIL_PATTERN = /^[^\s@]+@adobe\.com$/i;

function ManageBannerAdminsPage(): React.ReactElement {
  const [admins, setAdmins] = React.useState<BannerAdminUser[]>([]);
  const [newEmail, setNewEmail] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [resultDialog, setResultDialog] = React.useState<string | null>(null);
  const [emailPendingRemoval, setEmailPendingRemoval] = React.useState<string | null>(null);

  const refreshAdmins = () => {
    return getBannerAdmins()
      .then(setAdmins)
      .catch((err) => setResultDialog(err.message || 'Failed to load admins'));
  };

  React.useEffect(() => {
    refreshAdmins();
  }, []);

  const handleAdd = () => {
    setIsLoading(true);
    addBannerAdmin(newEmail.trim())
      .then(() => {
        setResultDialog('Admin added successfully');
        setNewEmail('');
        return refreshAdmins();
      })
      .catch((err) => setResultDialog(err.message || 'Failed to add admin'))
      .finally(() => setIsLoading(false));
  };

  const handleRemove = () => {
    if (!emailPendingRemoval) {
      return;
    }
    const email = emailPendingRemoval;
    setEmailPendingRemoval(null);
    setIsLoading(true);
    removeBannerAdmin(email)
      .then(() => {
        setResultDialog('Admin removed successfully');
        return refreshAdmins();
      })
      .catch((err) => setResultDialog(err.message || 'Failed to remove admin'))
      .finally(() => setIsLoading(false));
  };

  return (
    <div>
      <InformationDialog
        isOpen={resultDialog !== null}
        title="Manage Banner Admins"
        message={resultDialog || ''}
        variant="information"
        handleConfirmConfirmationDialog={() => setResultDialog(null)}
      />
      <ConfirmationDialog
        isOpen={emailPendingRemoval !== null}
        title="Remove Admin"
        message={`Are you sure you want to remove ${emailPendingRemoval} from Manage Banner admins?`}
        variant="destructive"
        confirmLabel="Remove"
        handleConfirmConfirmationDialog={handleRemove}
        handleDismissConfirmationDialog={() => setEmailPendingRemoval(null)}
      />

      <p className="banner-admins-scope-note">
        Admins here can only publish/edit the maintenance banner.
      </p>

      <Heading variant="subtitle2">Add Admin</Heading>
      <Grid container spacing={3}>
        <Grid item md={4} xs={12}>
          <TextField
            className="banner-admins-email-textfield"
            value={newEmail}
            placeholder="user@adobe.com"
            onChange={setNewEmail}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <Button
            variant="cta"
            disabled={!ADOBE_EMAIL_PATTERN.test(newEmail.trim()) || isLoading}
            onClick={handleAdd}
          >
            Add Admin
          </Button>
        </Grid>
      </Grid>

      <Heading variant="subtitle2">Current Admins</Heading>
      <table className="banner-admins-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Added By</th>
            <th>Added On</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {admins.map((admin) => (
            <tr key={admin.id}>
              <td>{admin.email}</td>
              <td>{admin.addedBy}</td>
              <td>{admin.createdDate}</td>
              <td>
                <Button
                  variant="negative"
                  disabled={isLoading || admins.length <= 1}
                  onClick={() => setEmailPendingRemoval(admin.email)}
                >
                  Remove
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ManageBannerAdminsPage;
